import { emails, fetchFlow, fetchFlowAnalytics } from '@/lib/api/email-flows';
import type { FlowSummary } from '@/lib/api/email-flows/types';
import { QUERY_KEYS, SENTRY_FEATURES } from '@/lib';
import { useStanQuery } from '@/queries';

interface UseFlowQueryProps {
    flowId: number;
    initialData?: FlowSummary;
}

/**
 * Query hook for fetching flow details
 */
export function useFlowQuery({ flowId, initialData }: UseFlowQueryProps) {
    return useStanQuery({
        queryKey: [QUERY_KEYS.FLOW_DETAILS, flowId],
        queryFn: () => fetchFlow(flowId),
        staleTime: 1000 * 60 * 10, // 10 minutes
        gcTime: 1000 * 60 * 15, // 15 minutes
        refetchOnWindowFocus: false,
        refetchOnMount: 'always',
        initialData,
        meta: {
            error: {
                sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-flow-details',
                title: 'Failed to load flow',
                message: 'Unable to load flow details. Please try again.',
                feature: SENTRY_FEATURES.EMAIL_FLOWS,
            },
        },
    });
}

interface UseEmailQueryProps {
    emailId: number;
}

/**
 * Query hook for fetching email details
 * Note: skipPageLoading is set to true to prevent blank screen when navigating between emails
 */
export function useEmailQuery({ emailId }: UseEmailQueryProps) {
    return useStanQuery(
        {
            queryKey: [QUERY_KEYS.EMAIL_DETAILS, emailId],
            queryFn: () => {
                // Guard against invalid emailId to prevent API calls with emailId=0
                if (!emailId || emailId <= 0) {
                    return Promise.resolve(null as any);
                }
                return emails.fetchFlowEmail(emailId);
            },
            enabled: emailId > 0,
            staleTime: 1000 * 60 * 10, // 10 minutes
            gcTime: 1000 * 60 * 15, // 15 minutes
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            meta: {
                error: {
                    sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-email-details',
                    title: 'Failed to load email',
                    message: 'Unable to load email details. Please try again.',
                    feature: SENTRY_FEATURES.EMAIL_FLOWS,
                },
            },
        },
        true // Skip page loading to prevent blank screen on navigation
    );
}

/**
 * Query hook for fetching flow analytics (sent counts per email)
 * Used in the email editor to determine if emails have been sent
 */
export function useFlowAnalyticsQuery() {
    return useStanQuery(
        {
            queryKey: [QUERY_KEYS.FLOW_ANALYTICS],
            queryFn: fetchFlowAnalytics,
            staleTime: 1000 * 60 * 5, // 5 minutes
            gcTime: 1000 * 60 * 10,
            refetchOnWindowFocus: false,
            meta: {
                error: {
                    sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-flow-analytics',
                    title: 'Failed to load analytics',
                    message: 'Unable to load flow analytics.',
                    feature: SENTRY_FEATURES.EMAIL_FLOWS,
                },
            },
        },
        true
    );
}

interface UseEmailTemplateQueryProps {
    templateType: string;
    enabled?: boolean;
}

/**
 * Query hook for fetching email template variables and defaults
 */
export function useEmailTemplateQuery({ templateType, enabled = true }: UseEmailTemplateQueryProps) {
    return useStanQuery(
        {
            queryKey: [QUERY_KEYS.EMAIL_TEMPLATE_VARIABLES, templateType],
            queryFn: () => emails.fetchEmailTemplateVariables(templateType),
            enabled,
            staleTime: 1000 * 60 * 30, // 30 minutes - templates don't change often
            gcTime: 1000 * 60 * 60, // 1 hour
            refetchOnWindowFocus: false,
            meta: {
                error: {
                    sentryMessage: '[EMAIL_FLOWS] failed-to-fetch-email-template',
                    title: 'Failed to load email template',
                    message: 'Unable to load email template. Please try again.',
                    feature: SENTRY_FEATURES.EMAIL_FLOWS,
                },
            },
        },
        true
    );
}
